import { fromJS, List, Map, Set } from "immutable";
import Device from "../../utils/models/Device";
import { v4DerivedV3Fields } from "../../utils/models/v4DeviceProperties";
import {
  SELECT_DEVICE,
  TOGGLE_DEVCON,
  WS_DATA_DEVICE,
  WS_DATA_DEVICE_PING,
  WS_DATA_DEVICES,
  SET_TRACK_SINGLE_DEVICE_ID,
  V4_ONLY_DEVICES_LOADED,
  SET_DEVICE_AVAILABILITY,
  SET_DEVICE_ONLINE,
  SET_DEVICE_LOCATION,
  V4_DEVICE_SAVED,
  SYNC_RUN_EVENT,
  V4_ARC_DEVICE_LINKS_LOADED,
  CHECK_V4_ARC_DEVICE_LINK,
  V4_ARC_DEVICE_LINK_CHECKED,
} from "../constants/device.constants";
import { AUTH_SUCCESS } from "../../containers/LoginPage/constants";
import { ARC_LINK_CHECK } from "../../utils/arcLink";
import {
  GET_DEVICE_SETTINGS_V4,
  UPDATE_DEVICE,
  SET_EMERIT_V4_STATUSES,
} from "../actions/deviceV4.actions";

// Websocket-written, never form-written — a save must not roll these back.
const WEBSOCKET_OWNED_FIELDS = [
  "status",
  "lastUpdate",
  "available",
  "_position",
  "disconnectReason",
];

// V4 devices carry the uuid as both `id` and `uuid`.
export const matchesV4Uuid = (uuid) => (d) =>
  !!d && (d.uuid === uuid || d.id === uuid);

// For V4 `lastUpdate` is the connection timestamp (see mapV4DeviceToV3) so it
// moves with the state; V3 rows keep theirs — there it means last activity.
export const applyOnline = (d, online, at, reason) => {
  const status = online ? "online" : "offline";
  let next = d.set("status", status);
  if (!d.isFromV4) return next;
  // Unknown (websocket event) clears it until REST restores it.
  next = next.set("disconnectReason", online ? null : reason || null);
  if (at) return next.set("lastUpdate", at);
  // No known time: the old one belongs to the other state.
  return d.get("status") !== status ? next.set("lastUpdate", 0) : next;
};

// For device lists outside this slice (admin grid and its cache). V4 rows only;
// returns the same list when nothing changed.
export const applyStatusAction = (devices, action) => {
  if (!action.uuid || !devices) return devices;
  const matches = matchesV4Uuid(action.uuid);
  let changed = false;
  const next = devices.map((d) => {
    if (!d?.isFromV4 || !matches(d)) return d;
    const updated =
      action.type === SET_DEVICE_ONLINE
        ? applyOnline(d, action.online, action.at, action.reason)
        : d.set("available", action.available);
    if (updated !== d) changed = true;
    return updated;
  });
  return changed ? next : devices;
};

export const initialState = fromJS({
  data: {},
  fetched: false,
  selectedDevice: null,
  trackSingleDeviceId: null,
  devConOpen: false,
  devConV4: null,
  emeritV4Statuses: {},
  // V4 uuids of devices linked to an ARC — their record has no `arcKey` to say so.
  v4ArcDeviceIds: Set(),
  // uuid -> ARC_LINK_CHECK; results land in v4ArcDeviceIds.
  v4ArcLinkChecks: Map(),
  v4OnlyDevices: List(),
  // Last V4 websocket sync-run event; drives SyncHistory's live refresh.
  syncRunEvent: null,
});

export default function deviceReducer(state: Map = initialState, action) {
  let devices = state.get("data");

  let dev;
  switch (action.type) {
    case AUTH_SUCCESS:
      return initialState;
    case WS_DATA_DEVICE_PING:
      if (!action.data) return state;
      dev = state.get("data").find((d) => d.id === action.data.id);
      dev = dev
        .set("lastUpdate", action.data.lastUpdate)
        .set("status", action.data.status);
      if (
        state.get("selectedDevice") &&
        dev.id === state.get("selectedDevice").id
      )
        state = state.set("selectedDevice", dev);
      return state.setIn(["data", action.data.id], dev);
    case WS_DATA_DEVICE:
      if (!action.data) return state;
      dev = new Device(action.data);
      if (
        state.get("selectedDevice") &&
        dev.id === state.get("selectedDevice").id
      )
        state = state.set("selectedDevice", dev);
      return state.setIn(["data", action.data.id], dev);
    case WS_DATA_DEVICES:
      // Ignore an empty snapshot so it can't wipe a device seeded for an open alarm.
      if (!action.data?.length) return state.set("fetched", true);
      devices = Map(
        action.data.map((d) => new Device(d)).map((d) => [d.id, d])
      );
      return state.set("data", devices).set("fetched", true);
    case SELECT_DEVICE: {
      // Reset the loaded V4 config on device change so the badge isn't stale.
      const deviceChanged =
        action.device?.id !== state.get("selectedDevice")?.id;
      return state
        .set("selectedDevice", action.device)
        .set("devConOpen", action.device ? state.get("devConOpen") : false)
        .set("devConV4", deviceChanged ? null : state.get("devConV4"));
    }
    case SET_TRACK_SINGLE_DEVICE_ID:
      return state.set("trackSingleDeviceId", action.deviceId);
    case TOGGLE_DEVCON:
      // Keep devConV4 so the card badge survives closing DevCon (reset on SELECT_DEVICE).
      return state.set("devConOpen", action.isOpen);
    case UPDATE_DEVICE:
      const selectedDevice = state.get("selectedDevice");
      if (!selectedDevice) return state;

      const updatedSelectedDevice = selectedDevice.merge(action.device);

      return state.set("selectedDevice", updatedSelectedDevice);
    case GET_DEVICE_SETTINGS_V4.SUCCESS:
      return state.set("devConV4", action.data.settings);
    case SET_EMERIT_V4_STATUSES:
      return state.set(
        "emeritV4Statuses",
        state.get("emeritV4Statuses").merge(fromJS(action.statuses))
      );
    case V4_ONLY_DEVICES_LOADED:
      return state.set("v4OnlyDevices", List(action.devices));
    case V4_ARC_DEVICE_LINKS_LOADED:
      return state.set("v4ArcDeviceIds", Set(action.deviceIds));
    case CHECK_V4_ARC_DEVICE_LINK:
      if (!action.uuid) return state;
      return state.setIn(["v4ArcLinkChecks", action.uuid], ARC_LINK_CHECK.PENDING);
    case V4_ARC_DEVICE_LINK_CHECKED: {
      const { uuid, linked } = action;
      if (!uuid) return state;
      if (linked == null) {
        return state.setIn(["v4ArcLinkChecks", uuid], ARC_LINK_CHECK.FAILED);
      }
      return state
        .setIn(["v4ArcLinkChecks", uuid], ARC_LINK_CHECK.LOADED)
        .update("v4ArcDeviceIds", (ids) =>
          linked ? ids.add(uuid) : ids.remove(uuid)
        );
    }
    case SET_DEVICE_AVAILABILITY: {
      const { uuid, available } = action;
      if (!uuid) return state;
      // V4 devices carry the uuid as both `id` and `uuid`.
      const matches = (d) => !!d && (d.uuid === uuid || d.id === uuid);

      const existing = state.get("data").find(matches);
      if (existing) {
        state = state.setIn(
          ["data", existing.id],
          existing.set("available", available)
        );
      }

      let v4Changed = false;
      const updatedV4 = (state.get("v4OnlyDevices") || List()).map((d) => {
        if (!matches(d)) return d;
        const updated = d.set("available", available);
        if (updated !== d) v4Changed = true;
        return updated;
      });
      if (v4Changed) state = state.set("v4OnlyDevices", updatedV4);

      const selected = state.get("selectedDevice");
      if (matches(selected)) {
        state = state.set("selectedDevice", selected.set("available", available));
      }

      return state;
    }
    case SET_DEVICE_ONLINE: {
      const { uuid, online, at, reason } = action;
      if (!uuid) return state;
      const matches = matchesV4Uuid(uuid);

      const existing = state.get("data").find(matches);
      if (existing) {
        state = state.setIn(
          ["data", existing.id],
          applyOnline(existing, online, at, reason)
        );
      }

      let v4Changed = false;
      const updatedV4 = (state.get("v4OnlyDevices") || List()).map((d) => {
        if (!matches(d)) return d;
        const updated = applyOnline(d, online, at, reason);
        if (updated !== d) v4Changed = true;
        return updated;
      });
      if (v4Changed) state = state.set("v4OnlyDevices", updatedV4);

      const selected = state.get("selectedDevice");
      if (matches(selected)) {
        state = state.set(
          "selectedDevice",
          applyOnline(selected, online, at, reason)
        );
      }

      return state;
    }
    case V4_DEVICE_SAVED: {
      const saved = action.device;
      if (!saved?.id) return state;
      // Identity only: a merged V3 row can share the uuid and must not be hit.
      const matches = (d) => !!d && d.id === saved.id;

      // The draft is forked at Edit-click, so carry websocket-owned fields over
      // from the stored record; derived v3 fields are recomputed from the save.
      const derived = v4DerivedV3Fields(saved.get("extraFields"));
      const applySaved = (d) =>
        WEBSOCKET_OWNED_FIELDS.reduce(
          (acc, key) => acc.set(key, d.get(key)),
          saved
        ).merge(derived);

      const existing = state.get("data").find(matches);
      if (existing)
        state = state.setIn(["data", existing.id], applySaved(existing));

      let v4Changed = false;
      const updatedV4 = (state.get("v4OnlyDevices") || List()).map((d) => {
        if (!matches(d)) return d;
        v4Changed = true;
        return applySaved(d);
      });
      if (v4Changed) state = state.set("v4OnlyDevices", updatedV4);

      const selected = state.get("selectedDevice");
      if (matches(selected))
        state = state.set("selectedDevice", applySaved(selected));

      return state;
    }
    case SET_DEVICE_LOCATION: {
      const { uuid, position } = action;
      if (!uuid || !position) return state;
      const matches = (d) => !!d && (d.uuid === uuid || d.id === uuid);
      // Don't let a late fetch from an earlier event overwrite a newer fix.
      const applyPosition = (d) => {
        const current = d.get("_position");
        if (current?.serverTime && position.serverTime < current.serverTime) {
          return d;
        }
        return d.set("_position", position);
      };

      const existing = state.get("data").find(matches);
      if (existing) {
        state = state.setIn(["data", existing.id], applyPosition(existing));
      }

      let v4Changed = false;
      const updatedV4 = (state.get("v4OnlyDevices") || List()).map((d) => {
        if (!matches(d)) return d;
        v4Changed = true;
        return applyPosition(d);
      });
      if (v4Changed) state = state.set("v4OnlyDevices", updatedV4);

      const selected = state.get("selectedDevice");
      if (matches(selected)) {
        state = state.set("selectedDevice", applyPosition(selected));
      }

      return state;
    }
    case SYNC_RUN_EVENT: {
      const prev = state.get("syncRunEvent");
      // Monotonic seq so consumers can tell a new event from a re-render.
      return state.set("syncRunEvent", {
        deviceId: action.deviceId,
        runId: action.runId,
        status: action.status,
        seq: ((prev && prev.seq) || 0) + 1,
      });
    }
    default:
      return state;
  }
}
